/**
 * The early-access form's state: values, which errors are shown, the
 * submission phase, and the focus the next render owes.
 *
 * The rules, from the reference:
 *
 *   * A field is validated once it has been *touched* — left with a value —
 *     or once a submit was attempted, which touches every field and shows
 *     the summary. Typing revalidates a touched field immediately.
 *   * The submit button looks disabled until the form is ready but stays
 *     clickable, so an attempt on an invalid form produces the summary and
 *     moves focus to it, rather than silently doing nothing.
 *   * One request at a time. A second submit while one is in flight is
 *     ignored. Typing after a failure returns the form to its normal phase.
 *   * Closing after success resets everything; closing otherwise keeps what
 *     was typed.
 *
 * Presentation directives exist for the review page and the tests: they put
 * the form into a state (partial, errors, submitting, failure, success)
 * without a network. Production passes none.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { Language } from '../../i18n/translations.js';
import type { SubmitEarlyAccess } from './api.js';
import {
  EMPTY_VALUES,
  FIELD_ORDER,
  invalidFields,
  type FieldName,
  type FormValues,
} from './validation.js';

export type Phase = 'form' | 'submitting' | 'error' | 'success';

export type DialogPresentation =
  'empty' | 'partial' | 'errors' | 'submitting' | 'failure' | 'success';

/** Where focus goes after the next render, and why. */
export type FocusRequest =
  | { readonly kind: 'summary'; readonly n: number }
  | { readonly kind: 'submit'; readonly n: number }
  | { readonly kind: 'field'; readonly field: FieldName; readonly n: number }
  | null;

export interface EarlyAccessForm {
  readonly values: FormValues;
  readonly phase: Phase;
  /** Every field that would be refused, in form order. */
  readonly invalid: readonly FieldName[];
  /** The subset of `invalid` whose message is shown right now. */
  readonly shownErrors: readonly FieldName[];
  readonly ready: boolean;
  /** The error summary is shown after an attempted invalid submit. */
  readonly summaryShown: boolean;
  readonly focusRequest: FocusRequest;
  readonly input: (field: FieldName, value: string) => void;
  readonly blur: (field: FieldName) => void;
  readonly submit: () => void;
  readonly focusField: (field: FieldName) => void;
  readonly reset: () => void;
}

interface FormState {
  readonly values: FormValues;
  readonly touched: ReadonlySet<FieldName>;
  readonly showAll: boolean;
  readonly phase: Phase;
  readonly focusRequest: FocusRequest;
}

const ALL_TOUCHED: ReadonlySet<FieldName> = new Set(FIELD_ORDER);

const INITIAL: FormState = {
  values: EMPTY_VALUES,
  touched: new Set(),
  showAll: false,
  phase: 'form',
  focusRequest: null,
};

/** The sample the reference review panel fills in, per language. */
export function sampleValues(language: Language): FormValues {
  return language === 'ar'
    ? {
        name: 'سارة العتيبي',
        email: 'sara@example.com',
        brand: 'مطبخ سارة',
        link: '@sarahskitchen',
      }
    : {
        name: 'Sarah Alotaibi',
        email: 'sarah@example.com',
        brand: 'Sarah’s Kitchen',
        link: '@sarahskitchen',
      };
}

function presented(presentation: DialogPresentation, language: Language): FormState {
  const sample = sampleValues(language);
  switch (presentation) {
    case 'empty':
      return INITIAL;
    case 'partial':
      return {
        ...INITIAL,
        values: { ...EMPTY_VALUES, name: sample.name, brand: sample.brand },
        focusRequest: { kind: 'field', field: 'email', n: 1 },
      };
    case 'errors':
      return {
        ...INITIAL,
        values: {
          name: 'Sarah',
          email: 'sarah@example',
          brand: 'Sarah’s Kitchen',
          link: 'not a link',
        },
        touched: ALL_TOUCHED,
        showAll: true,
        focusRequest: { kind: 'summary', n: 1 },
      };
    case 'submitting':
      return { ...INITIAL, values: sample, phase: 'submitting' };
    case 'failure':
      return { ...INITIAL, values: sample, phase: 'error' };
    case 'success':
      return { ...INITIAL, values: sample, phase: 'success' };
  }
}

export function useEarlyAccessForm({
  submit,
  language,
  presentation = null,
}: {
  readonly submit: SubmitEarlyAccess;
  readonly language: Language;
  readonly presentation?: DialogPresentation | null;
}): EarlyAccessForm {
  const [state, setState] = useState<FormState>(() =>
    presentation === null ? INITIAL : presented(presentation, language),
  );
  const inFlight = useRef<AbortController | null>(null);
  const requests = useRef(0);
  const nextRequest = () => {
    requests.current += 1;
    return requests.current;
  };

  useEffect(
    () => () => {
      inFlight.current?.abort();
    },
    [],
  );

  const invalid = invalidFields(state.values);
  const ready = invalid.length === 0;
  const shownErrors = invalid.filter(
    (field) => state.showAll || state.touched.has(field),
  );
  const summaryShown = state.showAll && invalid.length > 0;

  const input = useCallback((field: FieldName, value: string) => {
    setState((current) => ({
      ...current,
      values: { ...current.values, [field]: value },
      // Typing after a failure is the visitor trying again: the failure
      // notice goes, and the button is a submit button once more.
      phase: current.phase === 'error' ? 'form' : current.phase,
    }));
  }, []);

  const blur = useCallback((field: FieldName) => {
    setState((current) => {
      if (current.values[field].trim().length === 0 || current.touched.has(field)) {
        return current;
      }
      const touched = new Set(current.touched);
      touched.add(field);
      return { ...current, touched };
    });
  }, []);

  const focusField = useCallback((field: FieldName) => {
    setState((current) => ({
      ...current,
      focusRequest: { kind: 'field', field, n: nextRequest() },
    }));
  }, []);

  // The latest state, for the submit handler: the request is started outside
  // a state updater, so StrictMode's double-invoked updaters cannot start
  // it twice, and the in-flight controller is a synchronous guard against a
  // second click landing before the next render.
  const latest = useRef(state);
  latest.current = state;

  const doSubmit = useCallback(() => {
    const current = latest.current;
    if (current.phase === 'submitting' || inFlight.current !== null) return;
    if (invalidFields(current.values).length > 0) {
      setState((previous) => ({
        ...previous,
        touched: ALL_TOUCHED,
        showAll: true,
        focusRequest: { kind: 'summary', n: nextRequest() },
      }));
      return;
    }
    const controller = new AbortController();
    inFlight.current = controller;
    const payload = {
      name: current.values.name,
      email: current.values.email,
      brand: current.values.brand,
      link: current.values.link,
      language,
    };
    setState((previous) => ({ ...previous, phase: 'submitting', focusRequest: null }));
    void submit(payload, controller.signal).then((outcome) => {
      if (inFlight.current !== controller) return;
      inFlight.current = null;
      if (controller.signal.aborted) return;
      setState((previous) =>
        outcome.ok
          ? { ...previous, phase: 'success', focusRequest: null }
          : outcome.reason === 'aborted'
            ? { ...previous, phase: 'form' }
            : {
                ...previous,
                phase: 'error',
                focusRequest: { kind: 'submit', n: nextRequest() },
              },
      );
    });
  }, [language, submit]);

  const reset = useCallback(() => {
    inFlight.current?.abort();
    inFlight.current = null;
    setState(INITIAL);
  }, []);

  return {
    values: state.values,
    phase: state.phase,
    invalid,
    shownErrors,
    ready,
    summaryShown,
    focusRequest: state.focusRequest,
    input,
    blur,
    submit: doSubmit,
    focusField,
    reset,
  };
}
